<template>
    <div class="bg-white p-3 rounded-lg shadow-md">
        <div class="flex justify-between items-center mb-3">
            <h2 class="text-xl font-bold">{{ isEditing ? 'Edit Result Template' : 'Create Result Template' }}</h2>
            <button @click="$emit('close')" class="text-gray-500 hover:text-gray-700" data-testid="btn-close-template-builder">
                <i class="pi pi-times text-xl"></i>
            </button>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <!-- Left Panel: Template Settings -->
            <div class="lg:col-span-1 space-y-2">
                <div class="bg-gray-50 p-2.5 rounded-md border border-gray-200">
                    <h3 class="font-semibold mb-3">Template Settings</h3>

                    <div class="mb-3">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                        <InputText v-model="form.name" class="w-full" placeholder="e.g., Junior Secondary Standard" data-testid="input-template-name" />
                    </div>

                    <div class="mb-3">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Template Code</label>
                        <InputText v-model="form.code" class="w-full" placeholder="e.g., JSS_STD" data-testid="input-template-code" />
                    </div>

                    <div class="mb-3">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Grading System</label>
                        <Select v-model="form.grade_group_id" :options="gradeGroups" optionLabel="name" optionValue="id"
                            placeholder="Select a grading system" class="w-full" />
                    </div>

                    <div class="mb-3">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Template Style</label>
                        <Select v-model="form.style_code" :options="templateStyles" optionLabel="label" optionValue="value"
                            placeholder="Select a style" class="w-full" />
                    </div>

                    <div v-if="form.style_code === 'detailed'" class="mb-3">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Chart Data Column</label>
                        <Select v-model="form.chart_column" :options="availableColumnKeys"
                            placeholder="Select column for chart" class="w-full" />
                        <p class="text-[10px] text-gray-400 mt-1">Select the score column to plot in the performance chart.</p>
                    </div>

                    <div v-if="form.style_code === 'detailed'" class="mb-3">
                        <label class="block text-sm font-medium text-gray-700 mb-1">Chart Type</label>
                        <Select v-model="form.chart_type" :options="chartTypeOptions" optionLabel="label" optionValue="value"
                            placeholder="Select chart type" class="w-full" />
                        <p class="text-[10px] text-gray-400 mt-1">Select the default visualization type for the chart.</p>
                    </div>

                    <div v-if="isTertiary && assignedProgrammes.length > 0" class="mt-4 pt-4 border-t border-gray-100">
                        <h4 class="text-xs font-semibold text-gray-500 uppercase mb-2">Assigned Programmes</h4>
                        <div class="flex flex-wrap gap-2">
                           <span v-for="prog in assignedProgrammes" :key="prog.id"
                                class="bg-indigo-50 text-indigo-700 px-2 py-1 rounded text-xs border border-indigo-100">
                               {{ prog.program_name }}
                           </span>
                        </div>
                    </div>
                </div>

                <div class="bg-primary/10 p-2.5 rounded-md border border-primary/20 text-sm text-primary-700">
                    <p class="font-semibold mb-1">💡 Tip:</p>
                    <p>Build your report card by adding columns. Drag and drop to reorder or use the arrows.</p>
                </div>
            </div>

            <!-- Center Panel: Column List -->
            <div class="lg:col-span-2">
                <div class="flex justify-between items-center mb-3">
                    <h3 class="font-semibold">Columns ({{ columns.length }})</h3>
                    <div class="space-x-2">
                        <button @click="openAddColumnModal('ASSESSMENT')"
                            data-testid="btn-add-assessment-column"
                            class="bg-primary text-white px-3 py-1 rounded-full text-xs font-medium hover:bg-primary transition-colors shadow-sm">
                            + Assessment
                        </button>
                        <button @click="openAddColumnModal('CALCULATION')"
                            data-testid="btn-add-calculation-column"
                            class="bg-primary text-white px-3 py-1 rounded-full text-xs font-medium hover:bg-primary/90 transition-colors shadow-sm">
                            + Calculation
                        </button>
                        <button @click="openAddColumnModal('GRADE')"
                            data-testid="btn-add-grade-column"
                            class="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-medium hover:bg-orange-600 transition-colors shadow-sm">
                            + Grade
                        </button>
                        <button @click="openAddColumnModal('HISTORICAL')"
                            data-testid="btn-add-historical-column"
                            class="bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-medium hover:bg-purple-700 transition-colors shadow-sm">
                            + Previous Term
                        </button>
                    </div>
                </div>

                <!-- Tertiary Quick Add Buttons -->
                <div v-if="isTertiary" class="flex flex-wrap gap-2 mb-2 p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                    <span class="text-xs font-bold text-gray-400 uppercase tracking-wider w-full mb-1">Tertiary Columns</span>
                    <button @click="openAddColumnModal('UNIT')"
                        class="bg-teal-600 text-white px-3 py-1 rounded-full text-xs font-medium hover:bg-teal-700 transition-colors shadow-sm">
                        + Unit
                    </button>
                    <!-- Merged Grade Point into + Grade button -->
                    <button @click="openAddColumnModal('WEIGHTED_GRADE_POINT')"
                        class="bg-teal-600 text-white px-3 py-1 rounded-full text-xs font-medium hover:bg-teal-700 transition-colors shadow-sm">
                        + Weighted GP
                    </button>
                </div>

                <div v-if="columns.length === 0"
                    class="text-center py-10 border-2 border-dashed border-gray-300 rounded-lg text-gray-500">
                    No columns added yet. Start by adding an Assessment column.
                </div>

                <div v-else class="space-y-2" @dragover.prevent @drop="onDrop">
                    <div v-for="(col, index) in columns" :key="col.tempId" draggable="true"
                        @dragstart="onDragStart(index, $event)" @dragenter="onDragEnter(index)" @dragend="onDragEnd"
                        :class="[
                            'bg-white border border-gray-200 rounded-md p-3 flex items-center justify-between shadow-sm cursor-move transition-all duration-200',
                            draggedIndex === index ? 'opacity-50 dashed border-2 border-primary' : '',
                            dropTargetIndex === index && draggedIndex !== index ? 'border-t-4 border-primary' : ''
                        ]">
                        <div class="flex items-center space-x-3">
                            <div class="flex flex-col space-y-1 text-gray-400 cursor-grab active:cursor-grabbing">
                                <i class="pi pi-bars"></i>
                            </div>
                            <div>
                                <div class="flex items-center gap-2 mb-1">
                                    <span class="font-medium text-gray-900">{{ col.label }}</span>
                                    <span :class="getTypeBadgeClass(col.type)"
                                        class="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">{{
                                            col.type
                                        }}</span>
                                </div>
                                <div class="text-xs text-gray-500 flex items-center space-x-2">
                                    <span
                                        class="font-mono bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">key:
                                        {{ col.key }}</span>
                                    <span v-if="col.school_assessment_type" class="text-gray-500">
                                        Source: {{ col.school_assessment_type.name }}
                                    </span>
                                    <span v-if="col.type === 'CALCULATION'"
                                        class="text-gray-500 flex items-center gap-1">
                                        <span>{{ col.calculation_function }}({{
                                            getCalculationArgsDisplay(col.calculation_arguments) }})</span>
                                        <span v-if="hasSmartCalc(col.calculation_arguments)"
                                            class="bg-primary/10 text-primary px-1.5 py-0.5 rounded border border-primary/10 text-[10px]">
                                            Smart Calc
                                        </span>
                                    </span>
                                    <span v-if="col.type === 'GRADE'" class="text-gray-500">
                                        Source: {{ col.calculation_arguments?.source }}, Output: {{
                                            col.calculation_arguments?.output }}
                                    </span>
                                    <span v-if="col.type === 'HISTORICAL'" class="text-gray-500">
                                        Source Key: {{ col.calculation_arguments }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="flex items-center space-x-2">
                            <!-- Up/Down buttons -->
                            <div class="flex flex-col mr-2">
                                <button @click="moveColumn(index, -1)" :disabled="index === 0"
                                    :data-testid="'btn-move-column-up-' + index"
                                    class="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                                    <i class="pi pi-chevron-up text-[10px]"></i>
                                </button>
                                <button @click="moveColumn(index, 1)" :disabled="index === columns.length - 1"
                                    :data-testid="'btn-move-column-down-' + index"
                                    class="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                                    <i class="pi pi-chevron-down text-[10px]"></i>
                                </button>
                            </div>
                            <button @click="editColumn(index)" class="text-primary hover:text-primary-700" :data-testid="'btn-edit-column-' + index">
                                <i class="pi pi-pencil"></i>
                            </button>
                            <button @click="toggleVisibility(index)"
                                :class="col.is_visible ? 'text-gray-400 hover:text-gray-600' : 'text-red-400 hover:text-red-600'">
                                <i :class="col.is_visible ? 'pi pi-eye' : 'pi pi-eye-slash'"></i>
                            </button>
                            <button @click="removeColumn(index)" class="text-red-500 hover:text-red-700" :data-testid="'btn-remove-column-' + index">
                                <i class="pi pi-trash"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Feedbacks Section -->
                <div class="mt-3">
                    <div class="flex justify-between items-center mb-3">
                        <h3 class="font-semibold">Feedbacks ({{ feedbacks.length }})</h3>
                        <button @click="openAddFeedbackModal"
                            data-testid="btn-add-feedback-field"
                            class="bg-primary text-white px-4 py-1.5 rounded-md text-xs font-medium hover:bg-primary/90 shadow-sm transition-colors">
                            + Add Feedback Field
                        </button>
                    </div>

                    <div v-if="feedbacks.length === 0"
                        class="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 text-gray-400">
                        <i class="pi pi-comment text-4xl mb-3 text-gray-300"></i>
                        <p class="font-medium text-gray-500">No feedback fields defined.</p>
                        <p class="text-xs text-gray-400">e.g., 'Principal Comment', 'Staff Comment'.</p>
                    </div>

                    <div v-else class="space-y-2">
                        <div v-for="(fb, index) in feedbacks" :key="index"
                            class="bg-white border border-gray-200 rounded-md p-3 flex items-center justify-between shadow-sm">
                            <div class="flex items-center space-x-3">
                                <div class="flex flex-col space-y-1 text-gray-400 cursor-grab active:cursor-grabbing">
                                    <i class="pi pi-bars"></i>
                                </div>
                                <div>
                                    <div class="flex items-center gap-2">
                                        <span class="font-medium text-gray-900">{{ fb.label }}</span>
                                        <span v-if="fb.category"
                                            class="text-[10px] px-2 py-0.5 rounded uppercase font-semibold"
                                            :class="getCategoryBadgeClass(fb.category)">
                                            {{ getCategoryLabel(fb.category) }}
                                        </span>
                                    </div>
                                    <div class="text-xs text-gray-500 mt-0.5">Code: <span
                                            class="font-mono bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">{{
                                                fb.code }}</span></div>
                                    <div class="text-xs text-gray-500 mt-0.5" :data-testid="`feedback-type-${index}`">
                                        {{ feedbackTypeSummary(fb) }}</div>
                                </div>
                            </div>
                            <div class="flex items-center space-x-2">
                                <div class="flex flex-col mr-2">
                                    <button @click="moveFeedback(index, -1)" :disabled="index === 0"
                                        class="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                                        <i class="pi pi-chevron-up text-[10px]"></i>
                                    </button>
                                    <button @click="moveFeedback(index, 1)" :disabled="index === feedbacks.length - 1"
                                        class="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                                        <i class="pi pi-chevron-down text-[10px]"></i>
                                    </button>
                                </div>
                                <Button icon="pi pi-pencil" text rounded size="small" aria-label="Edit feedback field"
                                    :data-testid="`btn-edit-feedback-${index}`" @click="openEditFeedbackModal(index)" />
                                <button @click="removeFeedback(index)" class="text-red-500 hover:text-red-700">
                                    <i class="pi pi-trash"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Result Summaries Section -->
                <div class="mt-3">
                    <div class="flex justify-between items-center mb-3">
                        <h3 class="font-semibold">Result Summaries ({{ summaries.length }})</h3>
                        <button @click="openAddSummaryModal"
                            data-testid="btn-add-result-summary"
                            class="bg-primary text-white px-4 py-1.5 rounded-md text-xs font-medium hover:bg-primary/90 shadow-sm transition-colors">
                            + Add Summary
                        </button>
                    </div>

                    <div v-if="summaries.length === 0"
                        class="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 text-gray-400">
                        <i class="pi pi-chart-bar text-4xl mb-3 text-gray-300"></i>
                        <p class="font-medium text-gray-500">No result summaries defined.</p>
                        <p class="text-xs text-gray-400">e.g., 'Overall Percentage', 'Class Average'.</p>
                    </div>

                    <div v-else class="space-y-2">
                        <div v-for="(summary, index) in summaries" :key="index"
                            class="bg-white border border-gray-200 rounded-md p-3 shadow-sm">
                            <div class="flex items-center justify-between mb-2">
                                <div class="flex items-center space-x-3">
                                    <i class="pi pi-chart-bar text-emerald-500"></i>
                                    <div>
                                        <div class="font-medium text-gray-900">{{ summary.label }}</div>
                                        <div class="text-xs text-gray-500 mt-0.5">
                                            Key: <span class="font-mono bg-gray-100 px-1 py-0.5 rounded border border-gray-200">{{ summary.key }}</span>
                                            <span class="mx-2">|</span>
                                            <span class="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded text-[10px] uppercase">{{ summary.calculation_function }}</span>
                                            ({{ summary.source_key }})
                                            <span v-if="summary.max_score" class="text-gray-400 ml-2">max: {{ summary.max_score }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex items-center space-x-2">
                                    <button @click="editSummary(index)" class="text-primary hover:text-primary-700">
                                        <i class="pi pi-pencil"></i>
                                    </button>
                                    <button @click="removeSummary(index)" class="text-red-500 hover:text-red-700">
                                        <i class="pi pi-trash"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-3 flex justify-end space-x-3 border-t pt-2.5">
                    <button @click="$emit('close')" class="px-4 py-2 text-gray-600 hover:text-gray-800" data-testid="btn-cancel-template-save">Cancel</button>
                    <button @click="saveTemplate" :disabled="saving"
                        data-testid="btn-save-template"
                        class="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50">
                        {{ saving ? 'Saving...' : (isEditing ? 'Update Template' : 'Create Template') }}
                    </button>
                </div>
            </div>

            <!-- Add Column Modal -->
            <Dialog v-model:visible="showColumnModal"
                :header="`${isEditingColumn ? 'Edit' : 'Add'} ${activeColumnType} Column`" :modal="true"
                :style="{ width: '500px' }">
                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Column Label</label>
                        <InputText v-model="columnForm.label" class="w-full" placeholder="e.g., Final Grade" data-testid="input-template-column-label" />
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Unique Key</label>
                        <InputText v-model="columnForm.key" class="w-full" placeholder="e.g., final_grade" />
                        <p class="text-xs text-gray-500 mt-1">Used for calculations. Must be unique.</p>
                    </div>

                    <!-- Assessment Specifics -->
                    <div v-if="activeColumnType === 'ASSESSMENT'">
                        <div class="flex justify-between items-center mb-1">
                            <label class="block text-sm font-medium text-gray-700">Select Assessment</label>
                            <button @click="showCreateAssessmentModal = true"
                                class="text-xs text-primary hover:underline">+
                                Create New</button>
                        </div>
                        <Select v-model="columnForm.school_assessment_type_id" :options="groupedAssessments"
                            optionLabel="name" optionValue="id" optionGroupLabel="label" optionGroupChildren="items"
                            placeholder="Select an assessment..." class="w-full" />
                    </div>

                    <!-- Calculation Specifics -->
                    <div v-if="activeColumnType === 'CALCULATION'">
                        <div class="mb-3">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Function</label>
                            <Select v-model="columnForm.calculation_function" :options="calculationFunctions"
                                optionLabel="label" optionValue="value" class="w-full" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Input Columns</label>
                            <div class="border border-gray-300 rounded-md p-2 max-h-40 overflow-y-auto">
                                <div v-for="col in availableColumnsForCalc" :key="col.key"
                                    class="flex items-center mb-2">
                                    <Checkbox v-model="columnForm.calculation_inputs" :value="col.key"
                                        :inputId="col.key" />
                                    <label :for="col.key" class="ml-2 text-sm cursor-pointer">{{ col.label }} ({{
                                        col.key
                                        }})</label>
                                </div>
                                <div v-if="availableColumnsForCalc.length === 0" class="text-xs text-gray-500">No
                                    columns available above this one.</div>
                            </div>
                        </div>

                        <!-- Smart Calculation Toggle -->
                        <div class="flex items-start space-x-2 bg-amber-50 p-3 rounded-md border border-amber-100 mt-2">
                            <Checkbox v-model="columnForm.ignore_nulls" :binary="true" inputId="ignore_nulls" />
                            <div class="flex flex-col">
                                <label for="ignore_nulls" class="text-sm font-medium text-gray-700 cursor-pointer">
                                    Ignore Missing Values (Smart Calculation)
                                </label>
                                <p class="text-xs text-gray-500 mt-0.5">
                                    If checked, empty/pending scores are ignored instead of counting as 0.
                                    (e.g., Average of [60, Pending] becomes 60, not 30).
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Grade Specifics -->
                    <div v-if="activeColumnType === 'GRADE'">
                        <div class="mb-3">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Source Column</label>
                            <Select v-model="columnForm.grade_source" :options="availableColumnsForCalc"
                                optionLabel="label" optionValue="key" placeholder="Select source column"
                                class="w-full" />
                            <p class="text-xs text-gray-500 mt-1">The column to calculate the grade from (e.g.,
                                Total
                                Score).</p>
                        </div>
                        <div class="mb-3">
                            <label class="block text-sm font-medium text-gray-700 mb-1">Output Type</label>
                            <Select v-model="columnForm.grade_output"
                                :options="[
                                    { label: 'Grade (A, B, C...)', value: 'grade' },
                                    { label: 'Remark (Excellent...)', value: 'remark' },
                                    { label: 'Point Value (5.0, 4.0...)', value: 'point' }
                                ]"
                                optionLabel="label" optionValue="value" class="w-full" />
                        </div>
                    </div>

                    <!-- Tertiary Specifics -->
                    <div v-if="activeColumnType === 'UNIT'">
                       <p class="text-sm text-gray-500">This column represents the credit load for the course.</p>
                       <div class="mt-2 bg-yellow-50 p-2 text-xs text-yellow-800 rounded border border-yellow-200">
                           Key defaults to: <strong>unit</strong>
                       </div>
                    </div>

                    <div v-if="activeColumnType === 'WEIGHTED_GRADE_POINT'">
                         <div class="grid grid-cols-2 gap-2">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Unit Column Key</label>
                                <Select v-model="columnForm.unit_key" :options="availableColumnsForCalc"
                                    optionLabel="label" optionValue="key" placeholder="Select unit"
                                    class="w-full" />
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">Grade Point Key</label>
                                <Select v-model="columnForm.grade_point_key" :options="availableColumnsForCalc"
                                    optionLabel="label" optionValue="key" placeholder="Select GP"
                                    class="w-full" />
                            </div>
                         </div>
                         <div class="mt-2 bg-yellow-50 p-2 text-xs text-yellow-800 rounded border border-yellow-200">
                           Key defaults to: <strong>weighted_grade_point</strong>
                       </div>
                    </div>
                </div>

                <template #footer>
                    <div class="flex justify-end space-x-3">
                        <button @click="showColumnModal = false"
                            class="px-4 py-2 text-gray-600 hover:text-gray-800">Cancel</button>
                        <button @click="saveColumn"
                            class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90">
                            {{ isEditingColumn ? 'Update Column' : 'Add Column' }}
                        </button>
                    </div>
                </template>
            </Dialog>

            <!-- Create Assessment Modal -->
            <AssessmentCreationModal :isOpen="showCreateAssessmentModal" :academicCalendarId="currentAcademicCalendarId"
                @close="showCreateAssessmentModal = false" @created="handleAssessmentCreated" />
        </div>

        <!-- Add Feedback Modal -->
        <Dialog v-model:visible="showFeedbackModal"
            :header="editingFeedbackIndex !== null ? 'Edit Feedback Field' : 'Add Feedback Field'" :modal="true"
            :style="{ width: '440px', maxWidth: '95vw' }">
            <div class="space-y-4">
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Label</label>
                    <InputText v-model="feedbackForm.label" class="w-full" placeholder="e.g., Principal's Comment" />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Code (Generic/Unique)</label>
                    <InputText v-model="feedbackForm.code" class="w-full" placeholder="e.g., PRINCIPAL_COMMENT" />
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <div class="space-y-2">
                        <InputText v-model="feedbackForm.category" class="w-full"
                            placeholder="Type category (e.g., Affective)" />
                        <div class="flex flex-wrap gap-2">
                            <span v-for="opt in categoryOptions" :key="opt.value"
                                @click="feedbackForm.category = opt.value"
                                class="text-xs px-2 py-1 rounded cursor-pointer border transition-colors"
                                :class="feedbackForm.category === opt.value ? 'bg-primary/10 text-primary-700 border-primary/20' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'">
                                {{ opt.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <!-- How teachers answer this item (#574): a comment, or a rating on a scale. -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Answer type</label>
                    <SelectButton v-model="feedbackForm.input_type" :options="feedbackInputTypeOptions"
                        optionLabel="label" optionValue="value" :allowEmpty="false" data-testid="feedback-input-type" />
                </div>
                <template v-if="feedbackForm.input_type === ResultFeedbackInputType.Rating">
                    <div>
                        <label for="feedback-rating-max" class="block text-sm font-medium text-gray-700 mb-1">Scale (highest level)</label>
                        <InputNumber v-model="feedbackForm.rating_max" inputId="feedback-rating-max" :min="2" :max="10"
                            showButtons :useGrouping="false" data-testid="feedback-rating-max" />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Level labels (lowest first)</label>
                        <div class="space-y-2">
                            <div v-for="(_, i) in feedbackForm.rating_labels" :key="i" class="flex items-center gap-2">
                                <span class="w-6 text-right text-sm text-gray-500">{{ i + 1 }}</span>
                                <InputText v-model="feedbackForm.rating_labels[i]" class="flex-1"
                                    :data-testid="`feedback-rating-label-${i}`" />
                            </div>
                        </div>
                        <small class="text-gray-500">Leave every label blank to print the numbers only.</small>
                    </div>
                </template>
                <small v-if="feedbackFormError" class="text-red-500 block" data-testid="feedback-form-error">{{ feedbackFormError }}</small>
            </div>
            <template #footer>
                <div class="flex justify-end space-x-3">
                    <button @click="showFeedbackModal = false"
                        class="px-4 py-2 text-gray-600 hover:text-gray-800">Cancel</button>
                    <button @click="saveFeedback" data-testid="btn-save-feedback-field"
                        class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90">
                        {{ editingFeedbackIndex !== null ? 'Update' : 'Add' }} Feedback
                    </button>
                </div>
            </template>
        </Dialog>

        <!-- Add/Edit Summary Modal -->
        <Dialog v-model:visible="showSummaryModal" :header="editingSummaryIndex !== null ? 'Edit Summary' : 'Add Summary'" :modal="true"
            :style="{ width: '500px' }">
            <div class="space-y-4">
                <div class="grid grid-cols-2 gap-2">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Label</label>
                        <InputText v-model="summaryForm.label" class="w-full" placeholder="e.g., Overall %" />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Key</label>
                        <InputText v-model="summaryForm.key" class="w-full font-mono" placeholder="e.g., overall_percentage" />
                    </div>
                </div>
                <div class="grid grid-cols-2 gap-2">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Calculation Function</label>
                        <Select v-model="summaryForm.calculation_function" :options="['PERCENTAGE', 'AVERAGE', 'SUM', 'COUNT', 'GPA', 'CGPA']" class="w-full" />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Source Key</label>
                        <Select v-model="summaryForm.source_key" :options="availableColumnKeys" class="w-full" placeholder="Select column key" />
                    </div>
                </div>
                <div v-if="summaryForm.calculation_function === 'PERCENTAGE'">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Max Score</label>
                    <InputText v-model="summaryForm.max_score" class="w-full" placeholder="100" type="number" />
                    <small class="text-gray-400 text-xs">Maximum possible score for percentage calculation</small>
                </div>

                <!-- GPA/CGPA Specifics -->
                <div v-if="['GPA', 'CGPA'].includes(summaryForm.calculation_function)" class="grid grid-cols-2 gap-2">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Unit Column</label>
                        <Select v-model="summaryForm.unit_key" :options="availableColumnKeys" class="w-full" placeholder="Select unit column" />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Weighted GP Column</label>
                        <Select v-model="summaryForm.weighted_grade_point_key" :options="availableColumnKeys" class="w-full" placeholder="Select WGP column" />
                    </div>
                </div>
            </div>
            <template #footer>
                <div class="flex justify-end space-x-3">
                    <button @click="showSummaryModal = false"
                        class="px-4 py-2 text-gray-600 hover:text-gray-800">Cancel</button>
                    <button @click="saveSummary"
                        class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90">
                        {{ editingSummaryIndex !== null ? 'Update' : 'Add' }} Summary
                    </button>
                </div>
            </template>
        </Dialog>
    </div>
</template>

<script setup lang="ts">
import { useResultTemplateBuilder } from '@/composables/useResultTemplateBuilder';
import AssessmentCreationModal from './AssessmentCreationModal.vue';
import { useDragDropList } from '@/composables/common/useDragDropList';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { ResultFeedbackInputType } from '@/types/composition-functions';
import { FEEDBACK_INPUT_TYPE_OPTIONS, feedbackTypeSummary } from '@/utils/resultFeedbackRating';

const feedbackInputTypeOptions = [...FEEDBACK_INPUT_TYPE_OPTIONS];

const props = defineProps<{
    templateId?: string | null;
}>();

const { isTertiary } = useSchoolFeatures();

const emit = defineEmits(['close', 'saved']);

const {
  form,
  columns,
  saving,
  showColumnModal,
  activeColumnType,
  showCreateAssessmentModal,
  feedbacks,
  showFeedbackModal,
  feedbackForm,
  editingFeedbackIndex,
  feedbackFormError,
  assignedProgrammes,
  summaries,
  showSummaryModal,
  editingSummaryIndex,
  summaryForm,
  columnForm,
  editingColumnIndex,
  isEditingColumn,
  currentAcademicCalendarId,
  isEditing,
  gradeGroups,
  allAssessments,
  availableColumnKeys,
  genericAssessments,
  termAssessments,
  groupedAssessments,
  availableColumnsForCalc,
  chartTypeOptions,
  categoryOptions,
  calculationFunctions,
  templateStyles,
  openAddColumnModal,
  resetColumnForm,
  editColumn,
  saveColumn,
  getTypeBadgeClass,
  hasSmartCalc,
  getCalculationArgsDisplay,
  getCategoryBadgeClass,
  getCategoryLabel,
  toggleVisibility,
  handleAssessmentCreated,
  openAddFeedbackModal,
  openEditFeedbackModal,
  saveFeedback,
  removeFeedback,
  moveFeedback,
  openAddSummaryModal,
  editSummary,
  saveSummary,
  removeSummary,
  saveTemplate,
} = useResultTemplateBuilder(props, emit, isTertiary);

// Drag-drop for columns
const {
  draggedIndex,
  dropTargetIndex,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onDrop,
  moveItem: moveColumn,
  removeItem: removeColumn,
} = useDragDropList(columns, { sequenceKey: 'sequence' });
</script>
